import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
    PunchList,
    PunchListDetailsType,
    PunchListSiteMapType,
    PunchlistApiType,
    detailsSchema,
    siteMapSchema
} from '../types/PunchList/PunchListSchema';
import { zodKeys } from '../Helpers/Functions';

interface PunchListState {
    ProjectPunchLists: PunchlistApiType[];
    notSyncDeletedPunchLists: PunchlistApiType[];
    PunchListObject: PunchList;
    punchListDetails: Record<string, PunchListDetailsType>;
}

const initialState: PunchListState = {
    PunchListObject: {
        AssignedRootCauseText: '',
        AssignedRootCauseId: 0,
        AssignedDisciplineId: 0,
        CreatorDisciplineId: 0,
        CreatorRootCauseId: 0,
        CreatorRootCauseText: '',
        ProjectNumber: 0,
        Category: 0,
        Issue: 0,
        DrawingHash: '',
        SequenceControlArea: '',
        UnitDevice: '',
        IssueDescription: '',
        DefectPic1: '',
        DefectPic2: '',
        DueDate: '',
        Priority: 0,
        DoesPunchListRequireReinspection: true,
        ErmRelevant: false,
        ErmType: '',
        punchListId: ''
    },
    ProjectPunchLists: [],
    notSyncDeletedPunchLists: [],
    punchListDetails: {},
};

const PunchListSlice = createSlice({
    name: 'punchList',
    initialState,
    reducers: {
        addDetails: (state, action: PayloadAction<PunchListDetailsType>) => {
            state.PunchListObject = { ...state.PunchListObject, ...action.payload };
        },
        resetDetails: (state) => {
            for (const key of zodKeys(detailsSchema)) {
                state.PunchListObject[key] = '';
            }
        },
        addDefectPhoto: (state, action: PayloadAction<{ photoNumber: number; value: string, url: string, notSync?: boolean, creation?: boolean }>) => {
            state.PunchListObject[`DefectPic${action.payload.photoNumber}`] = action.payload.url;
            state.PunchListObject[`storedDefectPic${action.payload.photoNumber}`] = {
                notSync: action.payload.notSync,
                creation: action.payload.creation,
                value: action.payload.value
            };
        },
        addCorrectedPhoto: (state, action: PayloadAction<{ photoNumber: number; value: string, url: string, notSync?: boolean, creation?: boolean }>) => {
            state.PunchListObject[`CorrectedPic${action.payload.photoNumber}`] = action.payload.url;
            state.PunchListObject[`storedCorrectedPic${action.payload.photoNumber}`] = {
                notSync: action.payload.notSync,
                creation: action.payload.creation,
                value: action.payload.value
            };
        },
        addSiteMap: (state, action: PayloadAction<PunchListSiteMapType>) => {
            state.PunchListObject = { ...state.PunchListObject, ...action.payload };
        },
        addSiteMapInput: (state, action: PayloadAction<{ name: string; value: any }>) => {
            state.PunchListObject[action.payload.name] = action.payload.value;
        },
        resetSiteMap: (state) => {
            for (const key of zodKeys(siteMapSchema)) {
                state.PunchListObject[key] = '';
            }
        },
        resetPuncList: (state) => {
            state.PunchListObject = {};
        },
        addProjectPunchLists: (state, action: PayloadAction<PunchlistApiType[]>) => {
            const toSync = state.ProjectPunchLists.filter(item => item.notSync && item.creation);
            const toSyncEdit = state.ProjectPunchLists.filter(item => item.notSync && !item.creation);
            const plsMap = new Map<string, PunchlistApiType>(action.payload.map(item => [item.punchListUuid, item]));
            toSyncEdit.forEach(plToedit => {
                if (plsMap.has(plToedit.punchListUuid)) {
                    const currentPl = plsMap.get(plToedit.punchListUuid);
                    plsMap.set(plToedit.punchListUuid, { ...currentPl, ...plToedit });
                }
            });
            state.ProjectPunchLists = Array.from(plsMap.values()).concat(toSync);
        },
        updateDisciplinePunchlistsWithPhotos: (state, action: PayloadAction<{ disciplineId: number, punchlistsWithPhotos: PunchlistApiType[] }>) => {
            for (let i = 0; i < action.payload.punchlistsWithPhotos.length; i++) {
                const newPL = action.payload.punchlistsWithPhotos[i];
                const plIndex = state.ProjectPunchLists.findIndex(p => p.punchListUuid == newPL.punchListUuid);
                // Guard the index (0 is valid) and never overwrite a pending offline edit —
                // a background re-download must not clobber changes that haven't synced yet.
                if (plIndex >= 0 && !state.ProjectPunchLists[plIndex].notSync) {
                    state.ProjectPunchLists[plIndex] = newPL;
                }
            }
        },
        addProjectPunchList: (state, action: PayloadAction<PunchlistApiType>) => {
            state.ProjectPunchLists.push(action.payload);
        },
        addPunchListDetails: (state, action: PayloadAction<{ punchListId: string; details: PunchListDetailsType }>) => {
            const { punchListId, details } = action.payload;
            state.punchListDetails[punchListId] = details;
        },
        updatePunchlistItem: (state, action: PayloadAction<PunchlistApiType>) => {
            const selectedIndex = state.ProjectPunchLists.findIndex(p => p.punchListUuid === action.payload.punchListUuid);
            if (selectedIndex >= 0) {
                state.ProjectPunchLists[selectedIndex] = action.payload;
            }
        },
        deletePunchList: (state, action: PayloadAction<{ uuid: string, notSync: boolean }>) => {
            const { notSync, uuid } = action.payload;
            const index = state.ProjectPunchLists.findIndex(pl => pl.punchListUuid === uuid);
            if (index < 0) {
                console.error("PunchList not found while trying to delete", uuid);
                return;
            }
            if (notSync) {
                const PL = state.ProjectPunchLists[index];
                PL.notSync = true
                PL.deletion = true
                state.notSyncDeletedPunchLists.push(PL);
            }
            state.ProjectPunchLists = state.ProjectPunchLists
                .filter(pl => pl.punchListUuid?.toLocaleUpperCase() !== uuid?.toLocaleUpperCase());
        },
        removePLFromNotSyncDeletedPunchLists: (state, action: PayloadAction<{ uuid: string }>) => {
            const {  uuid } = action.payload;
            const index = state.notSyncDeletedPunchLists.findIndex(pl => pl.punchListUuid.toLocaleUpperCase() === uuid.toLocaleUpperCase());
            if (index < 0) {
                console.error("PunchList not found in notSyncDeletedPunchLists while trying to remove", uuid);
                return;
            }
            state.notSyncDeletedPunchLists = state.notSyncDeletedPunchLists.filter(pl => pl.punchListUuid.toLocaleUpperCase() !== uuid.toLocaleUpperCase());
        },
        rollbackPendingPunchList: (state, action: PayloadAction<{ uuid: string }>) => {
            const { uuid } = action.payload;
            const upperUuid = uuid?.toLocaleUpperCase?.();

            const activeIndex = state.ProjectPunchLists.findIndex(pl => pl.punchListUuid?.toLocaleUpperCase?.() === upperUuid);
            if (activeIndex >= 0) {
                const current = state.ProjectPunchLists[activeIndex];

                if (current.creation) {
                    state.ProjectPunchLists = state.ProjectPunchLists.filter(pl => pl.punchListUuid?.toLocaleUpperCase?.() !== upperUuid);
                    return;
                }

                const snapshot = current.rollbackSnapshot;
                if (snapshot) {
                    state.ProjectPunchLists[activeIndex] = {
                        ...current,
                        ...snapshot,
                        notSync: false,
                        creation: false,
                        deletion: false,
                        rollbackSnapshot: undefined,
                    } as PunchlistApiType;
                    return;
                }

                state.ProjectPunchLists[activeIndex] = {
                    ...current,
                    notSync: false,
                    creation: false,
                    deletion: false,
                    rollbackSnapshot: undefined,
                } as PunchlistApiType;
                return;
            }

            const deletedIndex = state.notSyncDeletedPunchLists.findIndex(pl => pl.punchListUuid?.toLocaleUpperCase?.() === upperUuid);
            if (deletedIndex >= 0) {
                const deletedItem = state.notSyncDeletedPunchLists[deletedIndex];
                state.notSyncDeletedPunchLists = state.notSyncDeletedPunchLists.filter(pl => pl.punchListUuid?.toLocaleUpperCase?.() !== upperUuid);
                state.ProjectPunchLists.push({
                    ...deletedItem,
                    notSync: false,
                    creation: false,
                    deletion: false,
                    rollbackSnapshot: undefined,
                });
            }
        }
    },
});

export const {
    addDetails,
    addSiteMap,
    resetDetails,
    addDefectPhoto,
    addCorrectedPhoto,
    // addStringfiedDefectPhoto,
    // addStringfiedCorrectedPhoto,
    updateDisciplinePunchlistsWithPhotos,
    addSiteMapInput,
    resetSiteMap,
    addProjectPunchLists,
    addProjectPunchList,
    resetPuncList,
    addPunchListDetails,
    updatePunchlistItem,
    deletePunchList,
    removePLFromNotSyncDeletedPunchLists,
    rollbackPendingPunchList,
} = PunchListSlice.actions;

export default PunchListSlice.reducer;